import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogContentText,
  Grow,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { fetchImportFieldCatalog, preflightGroupImport, submitGroupImport } from '@sophocles/api/dataImports';
import FileDropZone from '@sophocles/components/FileDropZone/FileDropZone';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import { addToast } from '@sophocles/features/toast';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import { parseApiError } from '@sophocles/utils/apiErrors';
import { formatBytes } from '@sophocles/utils/formatUtils';

import ColumnMappingFields from './ColumnMappingFields';
import {
  GROUP_IMPORT_ACCEPT,
  LOAD_SCOPE,
  SITE_SCOPE,
  WARNING_DISPLAY_LIMIT,
  applyColumnMapping,
  buildColumnMappings,
  canCommitPreview,
  fieldLabels,
  isMappingDirty,
  selectWarningsToShow,
  summarizeImportPreview,
  toFieldMapPayload,
  warningRowKey,
} from './groupImport';

// This surface talks about panels, so it overrides the shared relay-load
// default of "Face" per useRelayLoadTerm's own contract.
const LOAD_TERM_KEYS = { singularKey: 'terms.relayLoad.panel', pluralKey: 'terms.relayLoad.panels' };

function UploadGroupDataDialog({ customerId, siteGroupId }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const siteGroupTerm = useSiteGroupTerm();
  const siteTerm = useSiteTerm();
  const loadTerm = useRelayLoadTerm(LOAD_TERM_KEYS);

  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  const [catalog, setCatalog] = useState(null);

  const validationSchema = useMemo(
    () =>
      yup.object({
        site_group: yup
          .number()
          .required(t('dataImports.groupRequired', 'Select a target {{term}}.', { term: siteGroupTerm.singular })),
        file: yup.string().required(t('dataImports.fileRequired', 'Choose a document to upload.')),
      }),
    [t, siteGroupTerm.singular],
  );

  const formik = useFormik({
    initialValues: {
      site_group: siteGroupId || '',
      file: null,
      // One entry per column of the uploaded document, seeded from the preview
      // the reader answers with. Empty until there is a document to map.
      columns: [],
    },
    validationSchema,
    // The returned promise is load-bearing: formik clears isSubmitting only
    // when the submit it was handed settles, and ResponsiveDialog's own guard
    // lasts a single animation frame. Drop the promise and a second click
    // starts a second import.
    // eslint-disable-next-line no-use-before-define -- formik <-> handleSubmit form a submit-time cycle
    onSubmit: values => handleSubmit(values),
  });

  // The catalog is fetched once per opening rather than at mount because most
  // sessions never open this dialog, and once per opening rather than per
  // column because it is the same list for every one of them.
  useEffect(() => {
    if (!open || catalog != null) {
      return;
    }
    fetchImportFieldCatalog()
      .then(setCatalog)
      .catch(() => {
        // Without the catalog there are no fields to offer, so the dialog falls
        // back to naming the unrecognized columns and to whatever the reader
        // resolves on its own. A document under the headings the reader already
        // knows still imports; only mapping one by hand is lost.
      });
  }, [open, catalog]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setFiles([]);
    setPreview(null);
    setErrorMessages([]);
    setOpen(false);
  };

  // The preflight and the commit are handed the same body, so the mapping on
  // screen is the mapping that runs. A commit built without it would import
  // under the reader's own guess while the dialog showed the submitter's.
  const buildPayload = values => {
    const payload = new FormData();
    payload.append('file', files[0][0]);
    payload.append('entity_id', values.site_group);
    const fieldMap = toFieldMapPayload(values.columns);
    if (Object.keys(fieldMap).length > 0) {
      // Multipart carries text, so the mapping travels as JSON and the reader
      // parses it back.
      payload.append('field_map', JSON.stringify(fieldMap));
    }
    return payload;
  };

  // knownFields is [] deliberately: nothing here renders a backend field error
  // inline, and omitting it would file a flat validation dict under
  // fieldErrors, leaving the banner with nothing to say.
  const showFailure = error => {
    const { nonFieldMessages } = parseApiError(error, { knownFields: [], genericMessage: null });
    setErrorMessages(
      nonFieldMessages.length > 0
        ? nonFieldMessages
        : [t('dataImports.unknownError', 'The document could not be read. Try again, or pick a different file.')],
    );
  };

  // A failed read leaves the dialog open with its file still selected, so the
  // reader can fix the document and submit again without starting over.
  const handlePreflight = values => {
    setErrorMessages([]);
    setIsBusy(true);
    return preflightGroupImport(buildPayload(values))
      .then(nextPreview => {
        setPreview(nextPreview);
        // The picker is re-seeded from the answer rather than kept as it was,
        // so it shows what the reader resolved: a choice it could not honour —
        // a field an earlier column already claimed — comes back unapplied
        // instead of appearing to have taken effect.
        formik.setFieldValue('columns', buildColumnMappings(nextPreview, catalog));
      })
      .catch(showFailure)
      .finally(() => setIsBusy(false));
  };

  const handleCommit = values => {
    setErrorMessages([]);
    setIsBusy(true);
    return submitGroupImport(buildPayload(values))
      .then(() => {
        dispatch(
          addToast({
            title: t('general.success', 'Success'),
            message: t('dataImports.startedMessage', "Import started. You'll get an email when it finishes."),
            level: 'success',
            dismissible: true,
          }),
        );
        handleClose();
      })
      .catch(showFailure)
      .finally(() => setIsBusy(false));
  };

  const handleSubmit = values => (preview ? handleCommit(values) : handlePreflight(values));

  const removeFile = index => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    formik.setFieldValue('file', null);
  };

  const summary = summarizeImportPreview(preview);
  const { shown: warningsToShow, hiddenCount: hiddenWarningCount } = selectWarningsToShow(
    preview?.warnings,
    WARNING_DISPLAY_LIMIT,
  );
  const mappingColumns = formik.values.columns;
  const isCommittable = canCommitPreview(preview, mappingColumns);
  const isMappingEdited = preview != null && isMappingDirty(mappingColumns, preview);
  const canMapColumns = catalog != null && mappingColumns.length > 0;
  const missingRequiredLabels = fieldLabels(catalog, preview?.missing_required);
  const ambiguousLabels = fieldLabels(catalog, preview?.ambiguous);
  // A document that yields no structure has a cause the reader already named —
  // a required column nothing supplies — whenever there is one, and the
  // specific complaint is the useful one.
  const hasNothingToImport =
    preview != null && summary.siteCount === 0 && missingRequiredLabels.length === 0 && !isMappingEdited;
  const scopeLabels = { [SITE_SCOPE]: siteTerm.singular, [LOAD_SCOPE]: loadTerm.singular };
  const title = t('dataImports.uploadTitle', 'Upload {{term}} Data', { term: siteGroupTerm.singular });

  const handleColumnMappingChange = (heading, fieldName) => {
    formik.setFieldValue('columns', applyColumnMapping(mappingColumns, heading, fieldName));
  };

  const handleBackToFile = () => {
    setPreview(null);
    setErrorMessages([]);
    formik.setFieldValue('columns', []);
  };

  return (
    <Fragment>
      <Tooltip title={title}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClickOpen}
          aria-label={title}
          sx={{ minWidth: 0, px: 1, flexShrink: 0 }}
        >
          <UploadFileIcon fontSize="small" />
        </Button>
      </Tooltip>
      <ResponsiveDialog
        title={title}
        open={open}
        onSubmit={formik.submitForm}
        submitText={preview ? t('actions.confirm', 'Confirm') : t('actions.submit', 'Submit')}
        onClose={handleClose}
        canSubmit={
          !isBusy &&
          !formik.isSubmitting &&
          Object.keys(formik.errors).length === 0 &&
          formik.dirty &&
          (preview == null || isCommittable)
        }
        extraActions={
          preview != null ? (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleBackToFile}>
                {t('actions.back', 'Back')}
              </Button>
              {isMappingEdited && (
                <Button variant="contained" disabled={isBusy} onClick={() => handlePreflight(formik.values)}>
                  {t('dataImports.recheckMapping', 'Re-check')}
                </Button>
              )}
            </Stack>
          ) : null
        }
        sx={{ '& .MuiDialogContent-root > :not(style)': { mb: 2 } }}
      >
        <Stack spacing={2}>
          <FormErrorAlert messages={errorMessages} />
          {isBusy && (
            <Box>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {preview
                  ? t('dataImports.submittingImport', 'Starting the import…')
                  : t('dataImports.readingDocument', 'Reading the document…')}
              </Typography>
            </Box>
          )}
          {preview == null ? (
            <Fragment>
              <DialogContentText sx={{ mb: 0 }}>
                {t(
                  'dataImports.selectFileMessage',
                  'Select a target {{term}} and a document. Nothing is saved until you confirm the preview.',
                  { term: siteGroupTerm.singular.toLowerCase() },
                )}
              </DialogContentText>
              {!formik.values.site_group && (
                <SiteGroupAutocomplete
                  excludeInventory
                  required
                  fullWidth
                  name="site_group"
                  size="small"
                  disabled={isBusy}
                  customerId={customerId}
                  value={formik.values.site_group}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.site_group && Boolean(formik.errors.site_group)}
                  helperText={formik.touched.site_group && formik.errors.site_group}
                />
              )}
              <FileDropZone
                config={{ maxFiles: 1, accept: GROUP_IMPORT_ACCEPT }}
                onFilesAdded={uploadedFiles => {
                  uploadedFiles.forEach(fileData => {
                    const [uploadedFile, contents] = fileData;
                    setFiles([[uploadedFile, contents]]);
                    formik.setFieldValue('file', uploadedFile.name);
                  });
                }}
              >
                {({ isBusy: isDropzoneBusy }) => (
                  <Grow in={!isDropzoneBusy}>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'dataImports.acceptedFormats',
                        'Drag & drop an Excel (.xlsx) or CSV (.csv) document here, or click to browse',
                      )}
                    </Typography>
                  </Grow>
                )}
              </FileDropZone>
              {files.length > 0 && (
                <List dense disablePadding>
                  {files.map((selected, index) => (
                    <ListItem
                      key={selected[0].name}
                      sx={{ borderRadius: 'var(--radius-md)', bgcolor: 'action.hover', py: 0.5 }}
                      secondaryAction={
                        <IconButton size="small" edge="end" disabled={isBusy} onClick={() => removeFile(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <UploadFileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={selected[0].name}
                        secondary={formatBytes(selected[0].size)}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {catalog != null && catalog.length > 0 && (
                <Accordion disableGutters square variant="outlined" sx={{ borderRadius: 'var(--radius-md)' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t('dataImports.validColumnHeaders', 'Valid column headers ({{count}})', {
                        count: catalog.length,
                      })}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 2, rowGap: 0.25 }}>
                      {/* Column headings are the document's own text, matched
                          literally against the import field catalog, so they
                          are shown as the catalog supplies them. */}
                      {catalog.map(field => (
                        <Typography key={field.name} variant="caption" color="text.secondary">
                          • {field.label}
                          {field.required ? (
                            <Box component="span" sx={{ color: 'error.main' }}>
                              {' '}
                              *
                            </Box>
                          ) : null}
                        </Typography>
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {t('dataImports.requiredColumnsNote', 'Columns marked with an asterisk are required.')}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}
            </Fragment>
          ) : (
            <Fragment>
              <DialogContentText sx={{ mb: 0 }}>
                {t('dataImports.previewIntro', 'Review what this document will create, then confirm.')}
              </DialogContentText>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t(
                      'dataImports.summaryStructures',
                      '{{count}} {{term}}: {{newCount}} new, {{existingCount}} already in this {{groupTerm}}',
                      {
                        count: summary.siteCount,
                        term: siteTerm.plural.toLowerCase(),
                        newCount: summary.newSiteCount,
                        existingCount: summary.existingSiteCount,
                        groupTerm: siteGroupTerm.singular.toLowerCase(),
                      },
                    )}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary={t('dataImports.summaryLoads', '{{count}} {{term}}', {
                      count: summary.loadCount,
                      term: loadTerm.plural.toLowerCase(),
                    })}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                {summary.skippedRowCount > 0 && (
                  <ListItem disableGutters>
                    <ListItemText
                      primary={t('dataImports.summarySkipped', '{{count}} rows skipped', {
                        count: summary.skippedRowCount,
                      })}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                )}
              </List>
              {missingRequiredLabels.length > 0 && (
                <Alert severity="error">
                  <AlertTitle sx={{ mb: 0.5 }}>
                    {t('dataImports.missingRequiredTitle', 'Required columns not supplied')}
                  </AlertTitle>
                  <Typography variant="body2">
                    {t(
                      'dataImports.missingRequiredMessage',
                      'No column supplies {{fields}}. Point a column at each one below, then re-check.',
                      { fields: missingRequiredLabels.join(', ') },
                    )}
                  </Typography>
                </Alert>
              )}
              {ambiguousLabels.length > 0 && (
                <Alert severity="warning">
                  <AlertTitle sx={{ mb: 0.5 }}>
                    {t('dataImports.ambiguousTitle', 'More than one column claims the same field')}
                  </AlertTitle>
                  <Typography variant="body2">
                    {t(
                      'dataImports.ambiguousMessage',
                      'Two or more columns supply {{fields}}. The leftmost is used and the rest are dropped — give the others a different field below.',
                      { fields: ambiguousLabels.join(', ') },
                    )}
                  </Typography>
                </Alert>
              )}
              {canMapColumns ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('dataImports.columnMappingTitle', 'Column mapping')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {t(
                      'dataImports.columnMappingNote',
                      'Each column of the document and the field it fills. A column the import recognizes by its own name is always imported — give it a different field to change what it fills.',
                    )}
                  </Typography>
                  <ColumnMappingFields
                    columns={mappingColumns}
                    catalog={catalog}
                    disabled={isBusy}
                    onChange={handleColumnMappingChange}
                    scopeLabels={scopeLabels}
                  />
                </Box>
              ) : (
                preview.unmapped_columns?.length > 0 && (
                  <Alert severity="info">
                    <AlertTitle sx={{ mb: 0.5 }}>
                      {t('dataImports.unmappedColumnsTitle', 'Columns not recognized, and ignored')}
                    </AlertTitle>
                    <Typography variant="body2">{preview.unmapped_columns.join(', ')}</Typography>
                  </Alert>
                )
              )}
              {isMappingEdited && (
                <Alert severity="info">
                  <Typography variant="body2">
                    {t(
                      'dataImports.mappingChangedMessage',
                      'The mapping has changed. Re-check it to see what this document will create.',
                    )}
                  </Typography>
                </Alert>
              )}
              {warningsToShow.length > 0 && (
                <Alert severity="warning">
                  <AlertTitle sx={{ mb: 0.5 }}>
                    {t('dataImports.warningsTitle', '{{count}} warnings', { count: summary.warningCount })}
                  </AlertTitle>
                  <List dense disablePadding>
                    {warningsToShow.map((warning, index) => (
                      <ListItem key={warningRowKey(warning, index)} disableGutters sx={{ alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            warning.column
                              ? t('dataImports.warningAtColumn', 'Row {{row}}, column {{column}}', {
                                  row: warning.row,
                                  column: warning.column,
                                })
                              : t('dataImports.warningAtRow', 'Row {{row}}', { row: warning.row })
                          }
                          // The reader localizes a warning's message, so it is
                          // rendered as it arrived.
                          secondary={warning.message}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {hiddenWarningCount > 0 && (
                    <Typography variant="caption">
                      {t('dataImports.moreWarnings', 'and {{count}} more', { count: hiddenWarningCount })}
                    </Typography>
                  )}
                </Alert>
              )}
              {hasNothingToImport && (
                <Alert severity="error">
                  <Typography variant="body2">
                    {t(
                      'dataImports.nothingToImport',
                      'This document supplies nothing that can be imported. Map the columns above, or fix the document and upload it again.',
                    )}
                  </Typography>
                </Alert>
              )}
              {files.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                  <UploadFileIcon fontSize="small" />
                  <Typography variant="body2">{files[0][0].name}</Typography>
                  <Typography variant="caption">·</Typography>
                  <Typography variant="caption">{formatBytes(files[0][0].size)}</Typography>
                </Stack>
              )}
            </Fragment>
          )}
        </Stack>
      </ResponsiveDialog>
    </Fragment>
  );
}

export default UploadGroupDataDialog;
